import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calendar, Video, Save, CheckCircle2 } from "lucide-react";
import SessionTypesEditor from "./SessionTypesEditor";

export default function BookingsAdminTab() {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    const { data } = await api.get("/admin/bookings");
    setBookings(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = bookings.filter((b) => {
    if (filter === "upcoming") return b.status === "confirmed" && new Date(b.starts_at) > new Date();
    if (filter === "past") return new Date(b.starts_at) < new Date();
    if (filter === "cancelled") return b.status === "cancelled";
    return true;
  });

  const setDraft = (id, patch) => setDrafts({ ...drafts, [id]: { ...drafts[id], ...patch } });

  const save = async (id) => {
    const patch = drafts[id];
    if (!patch) return;
    try {
      await api.patch(`/admin/bookings/${id}`, patch);
      toast.success("Booking updated");
      setDrafts({ ...drafts, [id]: undefined });
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-5">
      <SessionTypesEditor
        compact
        title="Session types & pricing"
        subtitle="Set the price, duration, and title of each session Dr. Slava offers on the public Book a Session page."
      />

      <div>
        <h2 className="font-display text-2xl">Bookings ({bookings.length})</h2>
        <p className="text-sm text-white/55 mt-1 max-w-2xl">
          Paste each session's Zoom or Google Meet URL into the row. Members will see the "Join call" button on their dashboard when it's saved.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["all", "upcoming", "past", "cancelled"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            data-testid={`booking-filter-${f}`}
            className={`px-4 py-1.5 rounded-full text-xs capitalize ${
              filter === f ? "bg-gradient-to-r from-[#FF8A00] to-[#E52E71]" : "bg-[#0F0F0F] text-white/60 border border-white/5"
            }`}>{f}</button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map((b) => {
          const d = drafts[b.booking_id] || {};
          const dirty = Object.keys(d).length > 0;
          return (
            <div key={b.booking_id} data-testid={`booking-${b.booking_id}`}
              className="glass rounded-2xl p-5">
              <div className="flex flex-wrap items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#FF8A00]/25 to-[#E52E71]/10 flex items-center justify-center shrink-0">
                  <Calendar size={18} className="text-[#FF8A00]" />
                </div>
                <div className="flex-1 min-w-[240px]">
                  <div className="font-display text-lg">{b.session_type_title}</div>
                  <div className="text-sm text-white/60">{new Date(b.starts_at).toLocaleString()} · {b.duration_min} min</div>
                  <div className="text-[11px] text-white/40 mt-1">
                    {b.user_name} · {b.user_email} · ${(b.price_cents/100).toFixed(0)} ·{" "}
                    <span className={b.status === "confirmed" ? "text-[#FF8A00]"
                                    : b.status === "cancelled" ? "text-[#E52E71]" : "text-white/60"}>
                      {b.status}
                    </span>
                  </div>
                  {b.user_notes && (
                    <p className="text-xs text-white/55 mt-2 italic">"{b.user_notes}"</p>
                  )}
                </div>
                <Select value={d.status || b.status}
                  onValueChange={(v) => setDraft(b.booking_id, { status: v })}>
                  <SelectTrigger className="bg-[#0F0F0F] border-white/10 w-36 h-9 text-xs"
                    data-testid={`booking-status-${b.booking_id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="no_show">No-show</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-4 flex flex-wrap gap-2 items-center">
                <Video size={14} className="text-white/40" />
                <Input value={d.meeting_url ?? b.meeting_url ?? ""}
                  onChange={(e) => setDraft(b.booking_id, { meeting_url: e.target.value })}
                  placeholder="Paste Zoom / Google Meet URL"
                  data-testid={`booking-url-${b.booking_id}`}
                  className="bg-[#0F0F0F] border-white/10 flex-1 min-w-[240px] text-sm" />
                <Button onClick={() => save(b.booking_id)}
                  disabled={!dirty}
                  data-testid={`booking-save-${b.booking_id}`}
                  className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] disabled:opacity-40">
                  {dirty ? <><Save size={13} className="mr-1.5" /> Save</> : <><CheckCircle2 size={13} className="mr-1.5" /> Saved</>}
                </Button>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="glass rounded-2xl p-8 text-center text-white/40 text-sm">No bookings in this view.</div>
        )}
      </div>
    </div>
  );
}
